import AppointmentsState, {
    Scheduled, Confirmed, ConfirmedByPatient, Waiting, Doing, Done, Canceled, CanceledByPatient, Missed,
} from '../../consts/AppointmentsState';
import { getStatusColors, getStatusOverride, getPaletteColors } from '../../consts/AppointmentStatusColors';

/**
 * Etapas do atendimento (docs/features/appointment-steps): checklist que a
 * empresa cria dentro de um status do agendamento. Espelha
 * public/api/appointment-steps/AppointmentSteps.php (STATUSES e STATUS_ALIASES).
 */

const STATUS_HINTS = {
    [Scheduled]: 'Antes da confirmação: o que a equipe deixa pronto com antecedência.',
    [Confirmed]: 'Depois da confirmação: vale também para quem confirmou pelo link.',
    [Waiting]: 'Na chegada: o que conferir antes de chamar para o atendimento.',
    [Doing]: 'Durante o atendimento.',
    [Done]: 'Depois do atendimento: o fechamento do dia.',
    [Canceled]: 'Depois do cancelamento: vale também para quem cancelou pelo link.',
    [Missed]: 'Quando a pessoa não veio: o que fazer em seguida.',
};

const labelOf = (statusId) => AppointmentsState.States.find((state) => state.id === statusId)?.label || statusId;

export const STEP_STATUSES = [Scheduled, Confirmed, Waiting, Doing, Done, Canceled, Missed].map((id) => ({
    id,
    label: labelOf(id),
    hint: STATUS_HINTS[id],
}));

const STATUS_ALIASES = { [ConfirmedByPatient]: Confirmed, [CanceledByPatient]: Canceled };

/** Status cujas etapas valem para um agendamento neste status (null = nenhum) */
export const stepStatusFor = (appointmentStatus) => {
    const status = STATUS_ALIASES[appointmentStatus] || appointmentStatus;
    return STEP_STATUSES.some((item) => item.id === status) ? status : null;
};

/**
 * Progresso das etapas do agendamento no status atual.
 * Devolve null quando o status não tem etapas cadastradas.
 */
export const stepProgressFor = (appointment, allSteps) => {
    const status = stepStatusFor(appointment?.status);
    if (!status || !Array.isArray(allSteps)) return null;

    const steps = allSteps.filter((step) => step.Status === status);
    if (steps.length === 0) return null;

    const checks = {};
    (appointment.stepChecks || []).forEach((check) => { checks[check.stepId] = check; });

    const required = steps.filter((step) => step.IsRequired);
    const doneRequired = required.filter((step) => checks[step.Id]).length;
    const done = steps.filter((step) => checks[step.Id]).length;

    return {
        status,
        steps,
        checks,
        done,
        total: steps.length,
        doneRequired,
        totalRequired: required.length,
        // Só etapas opcionais: pronto quando todas foram marcadas
        ready: required.length > 0 ? doneRequired === required.length : done === steps.length,
    };
};

/** Rótulo de tudo pronto: na espera é "Pronto para chamar"; nos outros status, "Etapas concluídas" */
export const readyLabelFor = (status) => (status === Waiting ? 'Pronto para chamar' : 'Etapas concluídas');

/**
 * Cores do card: as da última etapa marcada que tem cor própria (na ordem do
 * cadastro), senão a cor que a empresa escolheu para o status. custom = false
 * quando vale a cor padrão (o card usa as classes de status do CSS).
 */
export const appointmentColorsFor = (appointment, allSteps) => {
    const progress = stepProgressFor(appointment, allSteps);
    const stepKey = progress
        ? [...progress.steps].reverse().find((step) => step.Color && progress.checks[step.Id] && getPaletteColors(step.Color))?.Color
        : null;
    const key = stepKey || getStatusOverride(appointment?.status);
    return key
        ? { colors: getPaletteColors(key), custom: true }
        : { colors: getStatusColors(appointment?.status), custom: false };
};

/** O selo de etapas no card só aparece de hoje em diante (o passado não pede mais nada) */
export const showsStepBadge = (appointment) => {
    const date = appointment?.date ? new Date(appointment.date) : null;
    if (!date || Number.isNaN(date.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
};
